import RelayPagination from 'components/RelayPagination'
import ChatThreadItem from 'components/ChatThreadItem'
import { CardContent, Chip, List, ListItem } from '@mui/material'
import { Check } from '@mui/icons-material'
import ContactForm from 'components/ChatMessage/ContactForm'
import ChatMessage from 'components/ChatMessage/ChatMessage'
import { usePaginationFragment } from 'react-relay'
import { graphql } from 'relay-runtime'
import { ChatThreadMessages_Messages$key } from '__generated__/ChatThreadMessages_Messages.graphql'
import { useFragment, useLazyLoadQuery } from 'react-relay/hooks'
import { ChatThreadMessages_Recipient$key } from '__generated__/ChatThreadMessages_Recipient.graphql'
import { useIsGuest } from 'hooks/user'
import { useQuickCommit } from 'hooks/useQuickCommit'
import { useDebouncedEffect } from 'hooks/debounce'
import { useChat } from 'hooks/chat'
import { useDataList, useEdges } from 'hooks/relay'
import { reverse } from 'lodash'
import React, { memo, useMemo } from 'react'
import { ChatThreadMessages_Thread$key } from '__generated__/ChatThreadMessages_Thread.graphql'
import { ChatThreadMessages_Query } from '__generated__/ChatThreadMessages_Query.graphql'
import { ChatThreadMessages_User$key } from '__generated__/ChatThreadMessages_User.graphql'

export const ChatThreadMessagesQuery = graphql`
  query ChatThreadMessages_Query($threadId: ID, $recipientId: ID, $initialMessageCount: Int) {
    currentUser {
      ...ChatThreadMessages_User
    }

    recipient: optionalNode(id: $recipientId) {
      ...ChatThreadMessages_Recipient
    }


    thread: optionalNode(id: $threadId) {
      ...ChatThreadMessages_Thread @arguments(initialMessageCount: $initialMessageCount)
    }
  }
`

export interface IChatThreadMessagesProps {
  threadId?: string
  recipientId?: string
  order: 'mostRecentAtTheTop' | 'mostRecentAtTheBottom'
  initialMessageCount?: number
}

function ChatThreadMessages(props: IChatThreadMessagesProps) {
  const { threadId, recipientId, order = 'mostRecentAtTheBottom', initialMessageCount = 5 } = props
  const data = useLazyLoadQuery<ChatThreadMessages_Query>(
    ChatThreadMessagesQuery, {
      threadId,
      recipientId,
      initialMessageCount,
    }, {
      fetchPolicy: 'store-and-network',
    },
  )
  const { peekMessage } = useChat()

  const thread = useFragment(graphql`
    fragment ChatThreadMessages_Thread on ChatThread
    @argumentDefinitions(
      initialMessageCount: { type: "Int", defaultValue: 5 },
    )
    {
      id
      purpose

      messagesNotFromSelf: chatMessages(filters: { excludeSelf: true }) {
        totalCount
      }

      unreadMessages: chatMessages(filters: { unread: true }) {
        totalCount
      }

      recipient {
        ...ChatThreadMessages_Recipient
      }

      ...ChatThreadMessages_Messages @arguments(count: $initialMessageCount)
    }
  `, data?.thread as ChatThreadMessages_Thread$key)

  const { data: paginatedData, ...pagination } = usePaginationFragment(graphql`
    fragment ChatThreadMessages_Messages on ChatThread
    @argumentDefinitions(
      count: { type: "Int", defaultValue: 5 },
      cursor: { type: "String" },
    )
    @refetchable(queryName: "ChatThreadMessages_chatMessages")
    {
      chatMessages(first: $count, after: $cursor)
      @connection(key: "ChatThreadMessages_chatMessages") {
        totalCount
        edges {
          node {
            id
            ...ChatMessage_Message
          }
        }
      }
    }
  `, thread as ChatThreadMessages_Messages$key)

  const recipient = useFragment(graphql`
    fragment ChatThreadMessages_Recipient on Recipient {
      ... on User {
        firstName
        isProfileFeedback
      }

      ... on ChatBot {
        name
      }

      ...ChatThreadItem_Contact
    }
  `, (data?.recipient || thread?.recipient) as ChatThreadMessages_Recipient$key)

  const user = useFragment(graphql`
    fragment ChatThreadMessages_User on User {
      email
      unconfirmedEmail
      integrationName

      ...user_useIsGuest
    }
  `, data?.currentUser as ChatThreadMessages_User$key)

  const isGuest = useIsGuest(user)

  const { execute: markRead } = useQuickCommit(graphql`
    mutation ChatThreadMessages_MarkReadMutation($input: MarkChatThreadReadInput!) {
      markChatThreadRead(input: $input) {
        chatThread {
          ...ChatDialogContent_Thread
        }

        currentUser {
          # Make sure chat bubble doesn't display unread messages anymore
          ...ChatBubble_User
        }
      }
    }
  `)

  useDebouncedEffect(() => {
    if (thread?.unreadMessages?.totalCount > 0) {
      // TODO: we should probably since an eventual mutation error to the user here
      markRead({ input: { chatThreadId: thread.id } })
    }
  }, 1000, [thread])

  // TODO: maybe we can avoid reversing the list, and reverse the display via CSS?
  const messageNodes = useEdges(paginatedData?.chatMessages?.edges)
  const messages = useDataList(() => order === 'mostRecentAtTheBottom' ? reverse([...(messageNodes || [])]) : messageNodes, [messageNodes, order])
  const email = user?.email || user?.unconfirmedEmail
  const isDirectWithSelf = Boolean(recipient) && !thread?.purpose
  const importedFromIntegration = Boolean(user?.integrationName)

  console.log('[DEBUG]', thread?.id, peekMessage, messages)

  const automatedMessage = useMemo(() => {
    const otherUserIsProfileFeedback = isDirectWithSelf && recipient?.isProfileFeedback
    const otherUserHasMessages = isDirectWithSelf && thread?.messagesNotFromSelf?.totalCount > 0
    const showProvideEmailMessage = messages.length > 0 && isGuest && isDirectWithSelf && !importedFromIntegration
    const showProfileFeedbackWelcome = otherUserIsProfileFeedback && (messages.length === 0 || !otherUserHasMessages) && !importedFromIntegration

    if (!recipient || (!showProfileFeedbackWelcome && !showProvideEmailMessage && !peekMessage)) return null

    let automatedMessages = [
      `Hi there and welcome! I'm ${recipient?.firstName}${otherUserIsProfileFeedback ? ' from Flexhire' : ''}.`,
      "If you have any questions, I'm here to help!",
    ]

    if (isGuest) {
      automatedMessages.push("If I'm not online when you are here, just drop me your email below and I'll reply as soon as I see it.")
    }

    if (peekMessage) {
      automatedMessages = [peekMessage]
    }

    return automatedMessages.join(' ')
  }, [thread, recipient, isDirectWithSelf, peekMessage])

  const paginationElement = (
    <RelayPagination
      infiniteScroll={false}
      hideWhenNoMoreResults
      perPage={10}
      firstPage={initialMessageCount}
      totalCount={paginatedData?.chatMessages?.totalCount}
      currentCount={messages.length}
      disableRefetch={!thread}
      {...pagination}
    />
  )

  return (
    <React.Fragment>
      {order === 'mostRecentAtTheBottom' ? paginationElement : null}

      {(automatedMessage) && (
        <ChatThreadItem
          message={automatedMessage}
          contactFragmentRef={recipient}
        >
          {isGuest && messages?.length > 0 && (
            <CardContent style={{ paddingTop: 0 }}>
              <List dense disablePadding>
                <ListItem disableGutters>
                  {email ? (
                    <Chip
                      size="small"
                      icon={<Check />}
                      label={`Email Saved: ${email}`}
                    />
                  ) : <ContactForm />}
                </ListItem>
              </List>
            </CardContent>
          )}
        </ChatThreadItem>
      )}

      {messages.map(chatMessage => (
        <ChatMessage
          key={chatMessage.id}
          messageFragmentRef={chatMessage}
        />
      ))}

      {order === 'mostRecentAtTheTop' ? paginationElement : null}
    </React.Fragment>
  )
}

export default memo(ChatThreadMessages)
